import { ComponentProps, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { pxToRem } from '@/shared/styles/themes'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { Box, Grid2 as Grid, Step, StepLabel, styled, Tab, Tabs, Typography } from '@mui/material'

import { Button, ButtonProps } from '../Button/Button'
import { Icon } from '../Icon/Icon'
import { DynamicFormField } from './DynamicFormField'
import { getDefaultValueByFieldType } from './libs/getDefaultValueByFieldType'
import { DynamicFieldData } from './models/DynamicFieldData'
import { DynamicFormDefinition } from './models/DynamicFormSection'
import { StyledFormAccordion, StyledFormAccordionDetails, StyledFormAccordionSummary } from './StyledFormAccordion'
import { StyledStepper } from './StyledFormStepper'

export type DynamicFormProps = Omit<ComponentProps<typeof StyledForm>, 'onSubmit'> & DynamicFormDefinition & {
  submitContent?: React.ReactNode
  initialValues?: Record<string, unknown>
  onStepperNavigate?: (step: number) => void // Only used for stepper form type
  onSubmit: (data: Record<string, unknown>) => void
  onCancel?: () => void
  isReadOnly?: boolean
  slotProps?: {
    submitButton?: ButtonProps
    cancelButton?: ButtonProps
  }
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  sections,
  additionalTabs,
  onSubmit,
  onStepperNavigate,
  onCancel,
  isReadOnly,
  initialValues,
  submitContent = 'Submit',
  formType = 'default',
  slotProps,
  ...props
}) => {
  const [activeStep, setActiveStep] = useState(0)
  const [activeTab, setActiveTab] = useState(0)
  const [expandedAccordion, setExpandedAccordion] = useState<number | false>(0)

  const allFields = useMemo(() => sections.flatMap((section) => section.fields), [sections])

  // Add default values to fields with dependencies so useWatch can set the values for the first render
  const processedFields = useMemo(() => {
    return allFields.map((field) => {
      if (!field?.dependencies) return field

      const updatedDependencies = field.dependencies.map((dep) => {
        if (!dep.defaultValue) {
          const dependency = allFields.find((f) => f.fieldName === dep.fieldName)

          if (dependency && dependency.hasOwnProperty('defaultValue')) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dependencyDefaultValue = (dependency as { defaultValue: any }).defaultValue

            return {
              ...dep,
              defaultValue: dependencyDefaultValue,
            }
          }
        }

        return dep
      })

      return {
        ...field,
        dependencies: updatedDependencies,
      }
    })
  }, [allFields])

  const processedSections = useMemo(() => {
    if (sections) {
      return sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          processedFields.find((pf) => pf.fieldName === field.fieldName) || field,
        ),
      }))
    }

    return null
  }, [sections, processedFields])

  const formMethods = useForm({
    defaultValues: processedFields.reduce<Record<string, unknown>>((acc, field) => {
      acc[field.fieldName] = getDefaultValueByFieldType(field.defaultValue, field.fieldType)

      return acc
    }, {}),
  })
  const { trigger, reset } = formMethods

  // Reset the form when the field initial values change
  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const renderFields = (fieldsToRender: DynamicFieldData[]) => (
    <Grid container columnSpacing={1}>
      {fieldsToRender.map((fieldProps, i) => (
        <DynamicFormField key={i} disabled={isReadOnly} {...fieldProps} />
      ))}
    </Grid>
  )

  if (processedFields.length === 0 && !additionalTabs) {
    return null
  }

  return (
    <StyledForm onSubmit={formMethods.handleSubmit(onSubmit)} {...props}>
      <FormProvider {...formMethods}>
        {formType === 'tabs' && processedSections ? (
          <>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              {processedSections.map((section, index) => (
                <Tab
                  disableRipple
                  key={index}
                  label={section.label}
                />
              ))}
              {additionalTabs && additionalTabs.map(({ label }, index) => (
                <Tab
                  disableRipple
                  key={index + processedSections.length}
                  label={label}
                />
              ))}
            </Tabs>
            {processedSections.map((section, index) => (
              <Box key={index} mt={2} minHeight={'100%'} display={activeTab === index ? 'block' : 'none'}>
                {renderFields(section.fields)}
              </Box>
            ))}
            {additionalTabs && additionalTabs.map(({ tabPanelContent }, index) => (
              <Box
                key={index + processedSections.length}
                display={activeTab === index + processedSections.length ? 'block' : 'none'}
                mt={2}
                minHeight={'100%'}
              >
                {tabPanelContent}
              </Box>
            ))}
          </>
        ) : formType === 'accordion' && processedSections ? (
          processedSections.map((section, index) => (
            <StyledFormAccordion
              key={index}
              expanded={expandedAccordion === index}
              onChange={(_, isExpanded) => setExpandedAccordion(isExpanded ? index : false)}
            >
              <StyledFormAccordionSummary
                expandIcon={<Icon sx={{ fontSize: pxToRem(16) }} icon={faChevronDown} />}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: pxToRem(14) }}>
                  {section.label}
                </Typography>
              </StyledFormAccordionSummary>
              <StyledFormAccordionDetails>
                {renderFields(section.fields)}
              </StyledFormAccordionDetails>
            </StyledFormAccordion>
          ))
        ) : formType === 'stepper' && processedSections ? (
          <>
            <StyledStepper activeStep={activeStep}>
              {processedSections.map((section, index) => (
                <Step key={index}>
                  <StepLabel
                    onClick={() => {
                      if (index >= activeStep) return

                      setActiveStep(index)
                      if (onStepperNavigate)
                        onStepperNavigate(index)
                    }}
                  >
                    {section.label}
                  </StepLabel>
                </Step>
              ))}
            </StyledStepper>
            {processedSections.map((section, index) => (
              <Box key={index} mt={2} minHeight={'100%'} display={activeStep === index ? 'block' : 'none'}>
                {renderFields(section.fields)}
              </Box>
            ))}
            <StyledFormActions>
              <Button
                text
                disabled={activeStep === 0}
                onClick={(ev) => {
                  ev.preventDefault()
                  ev.stopPropagation()

                  if (onStepperNavigate)
                    onStepperNavigate(activeStep - 1)
                  setActiveStep((prev) => prev - 1)
                }}
              >
                Back
              </Button>
              {activeStep === sections.length - 1 ? (
                <Button
                  primary
                  type="submit"
                  {...slotProps?.submitButton}
                >
                  {submitContent}
                </Button>
              ) : (
                <Button
                  outlined
                  onClick={(ev) => {
                    ev.preventDefault()
                    ev.stopPropagation()

                    // Validate the current step before moving to the next
                    const currentSectionFieldName = processedSections[activeStep].fields.map((field) => field.fieldName)

                    trigger(currentSectionFieldName).then((isValid) => {
                      if (isValid) {
                        if (onStepperNavigate)
                          onStepperNavigate(activeStep + 1)

                        setActiveStep((prev) => prev + 1)
                      }
                    })
                  }}
                >
                  Next
                </Button>
              )}
            </StyledFormActions>
          </>
        ) : (
          // Default or single-part form
          renderFields(allFields)
        )}
      </FormProvider>
      {formType !== 'stepper' && !isReadOnly && (
        <StyledFormActions>
          {onCancel && <Button text onClick={onCancel} {...slotProps?.cancelButton}>Cancel</Button>}
          <Button primary type="submit" {...slotProps?.submitButton}>
            {submitContent}
          </Button>
        </StyledFormActions>
      )}
    </StyledForm>
  )
}

const StyledForm = styled('form')`
  position: relative;
`

const StyledFormActions = styled('div')`
  position: sticky;
  bottom: ${(props) => props.theme.typography.pxToRem(-12)};
  left: 0;
  right: 0;
  z-index: 1001;
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  margin-top: ${(props) => props.theme.typography.pxToRem(24)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`